import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Badge, Button, Card, CardBody, Input, Page, Table, useToast } from "@theg1group/ui";
import type { TableColumn } from "@theg1group/ui";
import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { api } from "../lib/api.ts";

export function Brands() {
	const { domain } = useParams();
	return domain ? <BrandDetail domain={domain} /> : <BrandList />;
}

interface BrandRow {
	domain: string;
	brand_flag: string | null;
	messages: number;
	sources: number;
	last_seen: string | null;
}

const FLAGS = [
	{ v: "default", label: "Active" },
	{ v: "brand", label: "Marked" },
	{ v: "unmarked", label: "Unmarked" },
	{ v: "hidden", label: "Hidden" },
	{ v: "all", label: "All" },
];

function flagBadge(f: string | null) {
	if (f === "brand") return <Badge variant="success">brand</Badge>;
	if (f === "hidden") return <Badge variant="default">hidden</Badge>;
	return <Badge variant="info">landing</Badge>;
}

function BrandList() {
	const qc = useQueryClient();
	const toast = useToast();
	const [q, setQ] = useState("");
	const [flag, setFlag] = useState("default");

	const list = useQuery({
		queryKey: ["brands", q, flag],
		queryFn: () => api.get<{ rows: BrandRow[] }>(`/api/brands?flag=${flag}&q=${encodeURIComponent(q)}`),
	});
	const setFlagMut = useMutation({
		mutationFn: (v: { domain: string; brand_flag: string | null }) =>
			api.patch(`/api/brands/${encodeURIComponent(v.domain)}`, { brand_flag: v.brand_flag }),
		onSuccess: () => {
			toast.addToast("success", "Updated");
			qc.invalidateQueries({ queryKey: ["brands"] });
		},
		onError: (e) => toast.addToast("error", String(e)),
	});

	// Cycle: landing(null) → brand → hidden → null
	const cycle = (r: BrandRow) =>
		setFlagMut.mutate({ domain: r.domain, brand_flag: r.brand_flag === null ? "brand" : r.brand_flag === "brand" ? "hidden" : null });

	const columns: TableColumn<BrandRow>[] = [
		{
			id: "domain",
			header: "Landing brand",
			accessor: (r) => (
				<Link to={`/brands/${encodeURIComponent(r.domain)}`} className="text-primary hover:underline font-mono text-xs">
					{r.domain}
				</Link>
			),
		},
		{ id: "messages", header: "Messages", accessor: (r) => r.messages },
		{ id: "sources", header: "Sources", accessor: (r) => r.sources },
		{
			id: "last",
			header: "Last seen",
			accessor: (r) => (r.last_seen ? new Date(r.last_seen).toLocaleDateString() : "—"),
		},
		{
			id: "flag",
			header: "Mark",
			accessor: (r) => (
				<button type="button" onClick={() => cycle(r)} className="cursor-pointer">
					{flagBadge(r.brand_flag)}
				</button>
			),
		},
		{
			id: "graph",
			header: "",
			accessor: (r) => (
				<Link to={`/graph?focusBrand=${encodeURIComponent(r.domain)}`} className="text-primary text-sm hover:underline">
					graph →
				</Link>
			),
		},
	];

	return (
		<Page
			title="Landing brands"
			description="Конечные бренды — домены, куда приземляется трафик (где терминируются редирект-цепочки). Клик по метке: landing → brand → hidden."
		>
			<div className="mb-3 flex items-center gap-2">
				<div className="max-w-sm flex-1">
					<Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search brand domain…" />
				</div>
				<select
					value={flag}
					onChange={(e) => setFlag(e.target.value)}
					className="rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-content"
				>
					{FLAGS.map((f) => (
						<option key={f.v} value={f.v}>
							{f.label}
						</option>
					))}
				</select>
			</div>
			<Card>
				<div className="overflow-x-auto">
					<Table
						columns={columns}
						data={list.data?.rows ?? []}
						keyExtractor={(r) => r.domain}
						loading={list.isLoading}
						emptyMessage="No landing brands yet — detonate messages to populate landings."
					/>
				</div>
			</Card>
		</Page>
	);
}

interface BrandDetailData {
	brand: { name: string; brand_flag: string | null; rdap_registrar: string | null; rdap_created: string | null };
	hosts: Array<{ fqdn: string }>;
	messages: Array<{ id: string; subject: string | null; date_sent: string | null; from_domain: string | null; esp: string | null }>;
	sources: Array<{ from_domain: string | null; esp: string | null; messages: number }>;
	chains: string[][];
}

function BrandDetail({ domain }: { domain: string }) {
	const qc = useQueryClient();
	const toast = useToast();
	const { data } = useQuery({
		queryKey: ["brand", domain],
		queryFn: () => api.get<BrandDetailData>(`/api/brands/${encodeURIComponent(domain)}`),
	});
	const setFlag = useMutation({
		mutationFn: (brand_flag: string | null) => api.patch(`/api/brands/${encodeURIComponent(domain)}`, { brand_flag }),
		onSuccess: () => {
			toast.addToast("success", "Updated");
			qc.invalidateQueries({ queryKey: ["brand", domain] });
		},
	});

	if (!data) return <Page title={domain}>Loading…</Page>;

	return (
		<Page
			title={data.brand.name}
			description={`Landing brand · ${data.messages.length} message(s) land here`}
			actions={
				<div className="flex items-center gap-2">
					{flagBadge(data.brand.brand_flag)}
					<Button size="sm" variant="outline" onClick={() => setFlag.mutate("brand")}>
						Mark brand
					</Button>
					<Button size="sm" variant="outline" onClick={() => setFlag.mutate("hidden")}>
						Hide
					</Button>
					<Link to={`/graph?focusBrand=${encodeURIComponent(domain)}`} className="text-primary text-sm hover:underline self-center">
						graph →
					</Link>
				</div>
			}
		>
			<Section title={`Landing hosts (${data.hosts.length})`}>
				{data.hosts.map((h) => (
					<Link
						key={h.fqdn}
						to={`/domains/${encodeURIComponent(h.fqdn)}`}
						className="font-mono text-xs text-primary hover:underline mr-3"
					>
						{h.fqdn}
					</Link>
				))}
			</Section>

			<Section title={`Source campaigns (${data.sources.length})`}>
				{data.sources.map((s, i) => (
					<div key={`${s.from_domain}-${i}`} className="text-sm mb-0.5">
						<Badge variant="info">{s.esp ?? "—"}</Badge>{" "}
						<span className="font-mono text-content">{s.from_domain ?? "—"}</span>{" "}
						<span className="text-muted">· {s.messages} msg</span>
					</div>
				))}
			</Section>

			<Section title={`Messages / cases (${data.messages.length})`}>
				{data.messages.map((m) => (
					<div key={m.id} className="text-sm mb-0.5">
						<Link to={`/messages/${m.id}`} className="text-primary hover:underline">
							{m.subject ?? "(no subject)"}
						</Link>{" "}
						<span className="text-muted font-mono text-xs">{m.from_domain ?? ""}</span>
					</div>
				))}
			</Section>

			{data.chains.length > 0 && (
				<Section title={`Redirect chains (${data.chains.length})`}>
					{data.chains.map((hops, i) => (
						<div key={`chain-${i}`} className="font-mono text-xs text-content mb-0.5 break-all">
							{hops.map((h, j) => (
								<span key={`${i}-${j}`}>
									{j > 0 ? " → " : ""}
									{h}
									{j === hops.length - 1 ? " 🏁" : ""}
								</span>
							))}
						</div>
					))}
				</Section>
			)}
		</Page>
	);
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
	return (
		<Card className="mb-3.5">
			<CardBody>
				<h2 className="text-sm font-semibold text-content mb-2.5">{title}</h2>
				{children}
			</CardBody>
		</Card>
	);
}
